/**
 * One asset, in detail.
 *
 * The grid answers "which one is broken". This answers "what is wrong with it,
 * since when, and what has it been doing" — the question the predecessor's
 * camera card existed to answer, and the reason a grid on its own is not a
 * monitoring tool.
 *
 * Four tabs, because they are four different questions:
 *
 *   Status       what every check says right now, and how stale that is
 *   History      what it has been doing, as a timeline rather than a number
 *   Alerts       what it raised, when, and what an operator was told to do
 *   Capabilities what this device CAN be asked — the column the predecessor
 *                had nowhere to put
 *   Edit         change it — the half that did not exist until 2026-08-30
 *
 * ⚠️ Edit is LAST on purpose. The four before it are what this drawer is opened
 * for, usually while something is wrong; putting a form that can retire the
 * asset in front of them would be a write surface in the path of a diagnosis.
 */

import { RefreshCw } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import {
  ApiError,
  api,
  type AssetDetail as Detail,
  type DetailBackup,
  type DetailCapability,
  type DetailCheck,
  type NotificationRow,
  type NotificationsResponse,
} from './api.ts';
import {
  Banner,
  Button,
  DataTable,
  Drawer,
  Empty,
  Field,
  Loading,
  Pill,
  Section,
  Tabs,
  outcomeState,
  severityState,
  type ColumnDef,
  type PillState,
  type TabItem,
} from './components/index.ts';
import { AssetEditTab } from './AssetEdit.tsx';
import { type AssetRecord } from './asset-form.ts';
import { foldedLine, lastFrameLine, splitChecks } from './check-rows.ts';
import { KindPicture } from './KindPicture.tsx';
import { directLine, nxLine, sourcedLabel, type NxView } from './nx-sources.ts';
import { ASSET_TABS, unresolvableAssetLink, type AssetTab } from './route.ts';

const TABS = ['Status', 'History', 'Alerts', 'Capabilities', 'Edit'] as const;
type Tab = (typeof TABS)[number];

function isTab(value: string): value is Tab {
  return (TABS as readonly string[]).includes(value);
}

function ago(iso: string | null): string {
  if (iso === null) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/** Is this reading old enough that its outcome should not be believed? */
function isStale(lastChecked: string, staleAfterSeconds: number): boolean {
  return Date.now() - new Date(lastChecked).getTime() > staleAfterSeconds * 1000;
}

export function AssetDetailPanel({
  assetId,
  onClose,
  /**
   * True when this id came from the address bar rather than from the grid.
   *
   * Only changes what a 404 SAYS, and that difference is the point: a click on
   * a cell that 404s is a race worth reporting bluntly, while a link that 404s
   * is almost always the right camera under the wrong tenant, and needs to say
   * so. See `unresolvableAssetLink` in `route.ts`.
   */
  fromLink = false,
  section,
  onSectionChange,
}: {
  assetId: string;
  onClose: () => void;
  fromLink?: boolean;
  /**
   * The open section, held in the address (`#/fleet/<id>/history`) so a link
   * can name it and back returns to the previous one. Absent means Status.
   */
  section?: AssetTab | undefined;
  onSectionChange: (section: AssetTab) => void;
}): React.JSX.Element {
  const [detail, setDetail] = useState<Detail | null>(null);
  const tab: Tab = TABS[ASSET_TABS.indexOf(section ?? 'status')] ?? 'Status';
  const setTab = (next: Tab): void => onSectionChange(ASSET_TABS[TABS.indexOf(next)] ?? 'status');
  const [error, setError] = useState<string | null>(null);
  /** A 404 specifically, which is the tenant boundary rather than a fault. */
  const [unresolvable, setUnresolvable] = useState(false);
  /**
   * Bumped by the Edit tab after a write, to re-read the four read-only tabs.
   *
   * ⚠️ A refresh must NOT blank `detail` the way opening a different asset
   * does. The tabs are gated on `detail !== null`, so blanking would unmount
   * the Edit tab mid-save and take its "Saved." line — and any refusal the
   * server had just explained — down with it. Only a change of `assetId`
   * clears the panel.
   */
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setDetail(null);
    setError(null);
    setUnresolvable(false);
  }, [assetId]);

  useEffect(() => {
    let live = true;
    api
      .get<Detail>(`/api/assets/${assetId}/detail`)
      .then((d) => live && setDetail(d))
      .catch((err: unknown) => {
        if (!live) return;
        /*
         * 404 is the ONLY status that means "this tenant cannot open it". The
         * API answers it both for an asset belonging to someone else and for an
         * id that was never issued, deliberately — telling those apart would
         * make the endpoint an oracle for what exists elsewhere on the
         * platform. Anything else here (500, a dropped connection) is a fault
         * and must not be dressed up as a permission boundary.
         */
        if (err instanceof ApiError && err.status === 404) setUnresolvable(true);
        else setError(err instanceof Error ? err.message : 'failed');
      });
    return (): void => {
      live = false;
    };
  }, [assetId, reloadKey]);

  const linkFailure = unresolvable ? unresolvableAssetLink(assetId) : null;

  const openAlerts = detail === null ? 0 : detail.alerts.filter((a) => a.resolved_at === null).length;
  const tabs: TabItem[] = TABS.map((t) =>
    t === 'Alerts' ? { value: t, label: t, badge: openAlerts } : { value: t, label: t },
  );

  const title = detail?.asset.code ?? detail?.asset.name ?? (unresolvable ? 'Not available here' : 'Asset');

  return (
    <Drawer
      open
      onClose={onClose}
      title={title}
      width="lg"
      {...(detail !== null
        ? {
            subtitle: (
              <>
                {detail.asset.name} · {detail.asset.kind} ·{' '}
                {detail.asset.driver_id ?? 'no driver assigned'}
                {detail.asset.site_code !== null && ` · ${detail.asset.site_code}`}
              </>
            ),
          }
        : {})}
    >
      {error !== null && <Banner tone="fault">{error}</Banner>}
      {/*
        🔴 The drawer STAYS OPEN and says why.
        Closing it on a 404 would drop the reader on the fleet grid with no word
        said, which is the silent fallback this whole route exists to avoid —
        and the worst version of it, because they would then hunt for the camera
        the email named among a different tenant's similarly-coded sites.
      */}
      {linkFailure !== null && (
        <Banner
          tone="fault"
          title={fromLink ? linkFailure.title : 'That asset is no longer available'}
        >
          {fromLink
            ? linkFailure.detail
            : 'It was in the grid a moment ago and the server no longer returns it. ' +
              'It may have just been retired or reassigned; reload the fleet to catch up.'}
        </Banner>
      )}
      {detail === null && error === null && !unresolvable && (
        <Loading rows={6} cols={4} label="Loading asset" />
      )}

      {detail !== null && (
        <>
          <Tabs
            value={tab}
            onChange={(next) => {
              if (isTab(next)) setTab(next);
            }}
            items={tabs}
            label="Asset detail sections"
          />

          {tab === 'Status' && (
            <>
              {/* Only where a picture can exist. A host, a router or a recorder
                  declares no `snapshot`, and "No frame captured yet — once the
                  video check has run" on a Proxmox node promises a check that
                  will never run. */}
              {detail.capabilities.some((c) => c.key === 'snapshot' && c.declared) ? (
                <SnapshotPane
                  assetId={assetId}
                  faulted={detail.alerts.some((a) => a.resolved_at === null)}
                />
              ) : detail.asset.kind !== 'camera' ? (
                <div className="snapshot">
                  <KindPicture kind={detail.asset.kind} label={`${detail.asset.kind} ${detail.asset.name}`} size="large" />
                </div>
              ) : null}
              <StatusTab
                detail={detail}
                assetId={assetId}
                onChanged={() => setReloadKey((n) => n + 1)}
              />
            </>
          )}
          {tab === 'History' && <HistoryTab detail={detail} />}
          {tab === 'Alerts' && <AlertsTab detail={detail} />}
          {tab === 'Capabilities' && <CapabilitiesTab detail={detail} />}
          {tab === 'Edit' && (
            <AssetEditTab assetId={assetId} onChanged={() => setReloadKey((n) => n + 1)} />
          )}
        </>
      )}
    </Drawer>
  );
}

/**
 * The latest frame, large.
 *
 * `no-store` on the response and a cache-busting key on refresh, because a
 * cached snapshot on a monitoring screen is a picture of a camera that may have
 * stopped working since it was taken.
 */
function SnapshotPane({
  assetId,
  faulted,
}: {
  assetId: string;
  /** Whether this asset has an open alert. Decides if the pair is worth showing. */
  faulted: boolean;
}): React.JSX.Element {
  const [nonce, setNonce] = useState(0);
  const [missing, setMissing] = useState(false);
  const [noReference, setNoReference] = useState(false);

  if (missing) {
    return (
      <Empty
        title="No frame captured yet"
        body="A picture appears once the video check has run and returned a usable one."
      />
    );
  }

  /*
   * 🔴 The pair, and only when it says something.
   *
   * "How it looked when it worked" beside "how it looks now" is the highest
   * value picture this product can show: it is the difference between a camera
   * being broken, having been moved, having its lens covered, or the site light
   * having failed — distinctions no check here can make and a person makes at a
   * glance.
   *
   * Shown only while the asset is FAULTED. On a healthy camera the two frames
   * are the same picture minutes apart, which is visual noise that teaches
   * people to stop looking at the panel.
   *
   * ⚠️ And only when a reference frame actually exists. A camera faulted since
   * before it was ever observed healthy has none, and the missing half is left
   * missing — falling back to the current frame would put two identical
   * pictures under "when it worked" and "now", which is a confident claim that
   * nothing has changed, made about a camera nobody has seen working.
   */
  const showPair = faulted && !noReference;

  return (
    <div className="snapshot">
      {showPair ? (
        <div className="frame-pair">
          <figure>
            <img
              src={`/api/assets/${assetId}/reference-frame`}
              alt="The last frame captured while this camera had no open fault"
              onError={() => setNoReference(true)}
            />
            <figcaption className="small muted">When it last looked well</figcaption>
          </figure>
          <figure>
            <img
              src={`/api/assets/${assetId}/snapshot?v=${nonce}`}
              alt="Latest frame"
              onError={() => setMissing(true)}
            />
            <figcaption className="small muted">Now</figcaption>
          </figure>
        </div>
      ) : (
        <img
          src={`/api/assets/${assetId}/snapshot?v=${nonce}`}
          alt="Latest frame"
          onError={() => setMissing(true)}
        />
      )}
      {faulted && noReference && (
        <p className="small muted">
          No reference frame: this camera has not been seen without an open fault.
        </p>
      )}
      <Button size="sm" variant="ghost" icon={<RefreshCw size={14} />} onClick={() => setNonce((n) => n + 1)}>
        Refresh frame
      </Button>
    </div>
  );
}

/**
 * How long a registration reading stays believable.
 *
 * 🔴 Mirrors `BACKUP_STALE_AFTER_MINUTES` in `src/backup/trust.ts`, and it is a
 * mirror rather than a shared constant because the browser cannot import from
 * `src/`. `backup_panel.test.ts` asserts the two are equal, so the copy cannot
 * drift silently — which matters, because if this screen believed a row for
 * longer than the engine does it would show a confident green about footage the
 * alert engine had already stopped trusting.
 */
const BACKUP_STALE_AFTER_MINUTES = 30;

/** The one writing_state that means footage is demonstrably landing on disk. */
const BACKUP_GOOD = 'writing';

export function backupIsStale(observedAt: string, now: number = Date.now()): boolean {
  return now - new Date(observedAt).getTime() > BACKUP_STALE_AFTER_MINUTES * 60_000;
}

const BACKUP_COLUMNS: ColumnDef<DetailBackup, unknown>[] = [
  {
    id: 'recorder',
    header: 'Recorder',
    accessorKey: 'recorder_key',
    cell: ({ getValue }) => <span className="mono">{getValue() as string}</span>,
  },
  {
    id: 'held',
    header: 'Held',
    accessorFn: (b) => (b.registered ? (b.enabled ? 2 : 1) : 0),
    cell: ({ row }) => (
      <Pill state={row.original.registered ? 'ok' : 'fault'}>
        {row.original.registered ? (row.original.enabled ? 'yes' : 'not enabled') : 'no'}
      </Pill>
    ),
    size: 100,
  },
  {
    id: 'footage',
    header: 'Footage',
    accessorKey: 'writing_state',
    cell: ({ row }) => {
      /*
        🔴 A stale reading is shown as unknown, never as its last value. The
        alert engine stops trusting a registration after thirty minutes
        precisely because the reconciler writes rows only on a fully
        successful pass — so an aged row means nobody concluded anything
        recently, and rendering its last `writing` as green would be this
        screen claiming footage is landing on evidence the engine has already
        discarded.
      */
      const b = row.original;
      const stale = backupIsStale(b.observed_at);
      const writing = b.writing_state === BACKUP_GOOD;
      return (
        <Pill
          state={stale ? 'stale' : writing ? 'ok' : 'fault'}
          reason={
            stale
              ? `Not looked at for over ${BACKUP_STALE_AFTER_MINUTES} minutes. An aborted reconciler pass leaves the previous row untouched, so an aged row means nobody concluded anything recently.`
              : null
          }
        >
          {stale ? 'unknown' : b.writing_state}
        </Pill>
      );
    },
    size: 110,
  },
  {
    id: 'since',
    header: 'Since',
    accessorKey: 'writing_state_since',
    cell: ({ getValue }) => <span className="small muted">{ago(getValue() as string)}</span>,
    size: 100,
  },
  {
    id: 'looked',
    header: 'Last looked',
    accessorKey: 'observed_at',
    cell: ({ getValue }) => {
      const at = getValue() as string;
      const stale = backupIsStale(at);
      return (
        <span className={stale ? 'small' : 'small muted'}>
          {ago(at)}
          {stale && ' · stale'}
        </span>
      );
    },
    size: 120,
  },
];

/**
 * What the recorder is holding for this camera.
 *
 * ⚠️ An empty list means TWO things and this deliberately says both: the camera
 * is on no recorder, or nothing has looked. `backup_reconcile_runs` is what
 * separates them and it is platform-scoped, so this screen genuinely cannot
 * tell — and saying "not backed up" would be asserting the first when it might
 * be the second. That is the distinction migration 025 created two tables for.
 */
function BackupPane({ detail }: { detail: Detail }): React.JSX.Element | null {
  const rows = detail.backups;

  // Nothing to say about an asset no reconciler would ever consider.
  if (rows.length === 0) return null;

  return (
    <Section
      title="Backup"
      summary={`Written by the backup reconciler every ten minutes; a reading older than ${BACKUP_STALE_AFTER_MINUTES} minutes is not believed here or by the alert engine.`}
      flush
    >
      <DataTable
        data={rows}
        columns={BACKUP_COLUMNS}
        getRowId={(b) => b.recorder_key}
        caption="Recorders holding this camera"
      />
    </Section>
  );
}

/**
 * The name of a check, with everything about it that is not a status.
 *
 * Value, cost, what it requires, the description and the last definite answer
 * are all still here — behind a disclosure, because a seven-column table in a
 * 760px drawer was a table nobody read. The disclosure is per row so one check
 * can be opened while the rest stay a list.
 */
function CheckCell({ check, nx }: { check: DetailCheck; nx: NxView | null }): React.JSX.Element {
  const value = check.value_text ?? (check.value_num !== null ? String(check.value_num) : '—');
  return (
    <div>
      <div>{sourcedLabel(check.check_key, check.label, nx)}</div>
      <div className="cell-sub">{check.check_key}</div>
      <details className="small muted">
        <summary>More</summary>
        {check.description !== null && <div>{check.description}</div>}
        <div>
          Value: <span className="mono">{value}</span>
        </div>
        {/* The column the predecessor could not have. A check returning
            nothing usable for three days is a different problem from one
            that is failing. */}
        <div className={check.last_definite === null ? 'sev-warning' : undefined}>
          Last definite answer: {check.last_definite === null ? 'never' : ago(check.last_definite)}
        </div>
        <div>Cost: {check.cost}</div>
        <div>Requires: {check.requires}</div>
      </details>
    </div>
  );
}

/**
 * Where this camera's answers come from, when NX records it (070): CosMon's
 * own connection and NX's view, on separate lines, so "no answer" from one is
 * never read as the other's word. See nx-sources.ts.
 */
function NxSourcesPane({ detail }: { detail: Detail }): React.JSX.Element | null {
  const nx = detail.nx ?? null;
  if (nx === null) return null;
  const reachable = detail.checks.find((c) => c.check_key === 'reachable');
  const direct = directLine(nx, reachable);
  const fromNx = nxLine(nx);
  const rows = [
    {
      source: 'Direct connection',
      via: 'CosMon → site router → camera',
      line: direct,
      when:
        nx.linked && reachable !== undefined
          ? `last answered ${reachable.last_definite === null ? 'never' : ago(reachable.last_definite)}`
          : null,
    },
    {
      source: 'NX Witness',
      via:
        `${nx.server_label}${nx.name !== null ? ` · “${nx.name}”` : ''}` +
        (nx.failover_home != null ? ` · on a failover server (normally ${nx.failover_home})` : ''),
      line: fromNx,
      when: nx.reported_at === null ? null : `as of ${ago(nx.reported_at)}`,
    },
  ];
  return (
    <Section title="How CosMon hears about this camera">
      <table className="dense nx-sources">
        <tbody>
          {rows.map((r) => (
            <tr key={r.source}>
              <td>
                <div className="cell-strong">{r.source}</div>
                <div className="small muted">{r.via}</div>
              </td>
              <td>
                <Pill state={r.line.state}>{r.line.headline}</Pill>
              </td>
              <td className="small">
                {r.line.detail !== null && <div>{r.line.detail}</div>}
                {r.when !== null && <div className="muted">{r.when}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Section>
  );
}

function StatusTab({
  detail,
  assetId,
  onChanged,
}: {
  detail: Detail;
  assetId: string;
  /** Re-fetch after a mute, so the row reflects it rather than the caller's memory. */
  onChanged: () => void;
}): React.JSX.Element {
  const columns = useMemo<ColumnDef<DetailCheck, unknown>[]>(
    () => [
      {
        id: 'check',
        header: 'Check',
        accessorKey: 'label',
        cell: ({ row }) => <CheckCell check={row.original} nx={detail.nx ?? null} />,
      },
      {
        id: 'state',
        header: 'State',
        accessorKey: 'outcome',
        cell: ({ row }) => {
          // An NX camera's video_present never fails — a pass is "a frame
          // arrived", and silence goes stale rather than red — so a green tick
          // there would promise more than it knows. See check-rows.ts.
          const frame = lastFrameLine(row.original, detail.asset.driver_id);
          if (frame !== null) return <span className="small muted">{frame}</span>;
          return (
            <Pill state={outcomeState(row.original.outcome)} reason={row.original.reason}>
              {row.original.outcome}
            </Pill>
          );
        },
        size: 110,
      },
      {
        // `since` moves only when the OUTCOME changes, so this reads
        // "failing for 13 days" rather than just "failing".
        id: 'since',
        header: 'Since',
        accessorKey: 'since',
        cell: ({ getValue }) => <span className="small">{ago(getValue() as string)}</span>,
        size: 100,
      },
      {
        id: 'checked',
        header: 'Last checked',
        accessorKey: 'last_checked',
        cell: ({ row }) => {
          const stale = isStale(row.original.last_checked, row.original.stale_after_s);
          return (
            <span className={stale ? 'small sev-warning' : 'small muted'}>
              {ago(row.original.last_checked)}
              {stale && ' · stale'}
            </span>
          );
        },
        size: 120,
      },
      {
        id: 'notify',
        header: 'Notify',
        enableSorting: false,
        cell: ({ row }) => (
          <MuteControl assetId={assetId} checkKey={row.original.check_key} onChanged={onChanged} />
        ),
        size: 130,
      },
    ],
    [assetId, onChanged, detail.nx, detail.asset.driver_id],
  );
  /*
   * Every check kind is still a row in the data. The ones this driver cannot
   * ask at all — `unavailable` with the engine's "driver … does not declare
   * capability …" sentence — are folded into one counted line rather than
   * shown seven times per NX camera. A check that is unavailable for a real
   * reason stays in the table. See check-rows.ts.
   */
  const { shown, folded } = useMemo(() => splitChecks(detail.checks), [detail.checks]);

  if (detail.checks.length === 0) {
    return (
      <>
        <BackupPane detail={detail} />
        <SystemDrivePane detail={detail} assetId={assetId} onChanged={onChanged} />
        <Empty title="Nothing checked yet" body="No check has run against this asset." />
      </>
    );
  }
  return (
    <>
      <NxSourcesPane detail={detail} />
      <BackupPane detail={detail} />
      <SystemDrivePane detail={detail} assetId={assetId} onChanged={onChanged} />
      <Section flush>
        <DataTable
          data={shown}
          columns={columns}
          getRowId={(c) => c.check_key}
          caption="Current state of every check"
        />
      </Section>
      {folded.length > 0 && (
        <details className="fold">
          <summary>{foldedLine(folded.length, detail.asset.driver_id)}</summary>
          <div className="fold-body">
            <div className="small muted">
              The device is not broken; this driver has no way to ask. Each resolves to “unavailable” rather than passing or failing.
            </div>
            <ul className="small">
              {folded.map((c) => (
                <li key={c.check_key}>
                  {c.label} <span className="cell-sub">{c.check_key}</span>
                </li>
              ))}
            </ul>
          </div>
        </details>
      )}
    </>
  );
}

/** Kinds a host agent runs on: the alert rules' HOST_KINDS. */
const HOST_KINDS = new Set(['server', 'nvr', 'recorder', 'desktop', 'laptop']);

/**
 * The "Single-drive recorder" switch, beside the system-drive reading it governs.
 *
 * It began life (2026-10-20) under Exemptions on the Edit tab, where nobody
 * looking at a 91%-full system drive would think to go. It belongs where the
 * reading is: a host's Status view, only for the kinds an agent reports a
 * system drive on. The detail payload does not carry `system_drive_records`,
 * so the current setting is read from the asset record itself.
 */
function SystemDrivePane({
  detail,
  assetId,
  onChanged,
}: {
  detail: Detail;
  assetId: string;
  onChanged: () => void;
}): React.JSX.Element | null {
  const isHost = HOST_KINDS.has(detail.asset.kind);
  const [on, setOn] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!isHost) return;
    let live = true;
    api
      .get<AssetRecord>(`/api/assets/${assetId}`)
      .then((record) => { if (live) setOn(record.system_drive_records === true); })
      .catch((err: unknown) => { if (live) setError(err instanceof ApiError ? err.message : String(err)); });
    return () => { live = false; };
  }, [assetId, isHost]);

  if (!isHost) return null;

  const reading = detail.checks.find((c) => c.check_key === 'system_drive_capacity');
  const readingText = reading?.value_text ?? (reading?.value_num !== null && reading?.value_num !== undefined ? `${reading.value_num}% full` : null);

  async function write(next: boolean): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.put(`/api/assets/${assetId}/system-drive-records`, { on: next });
      setOn(next);
      setNotice(next ? 'Marked single-drive. The system drive warning clears on the next report.' : 'The system drive is judged again.');
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section
      title="System drive"
      summary={readingText !== null ? `Last reading: ${readingText}.` : 'No system-drive reading yet.'}
    >
      {error !== null && <Banner tone="fault">{error}</Banner>}
      {notice !== null && <Banner tone="ok">{notice}</Banner>}
      <Field
        id="system-drive"
        label="Single-drive recorder"
        hint={
          on === true
            ? 'On. NX records to the system drive here, so it is expected to be near full and is not judged.'
            : 'Off. The system drive warns at 90% full. Turn on for a basic one-drive box where NX records to C: on purpose.'
        }
      >
        <input
          id="system-drive"
          type="checkbox"
          checked={on === true}
          disabled={busy || on === null}
          onChange={(event) => void write(event.target.checked)}
        />
      </Field>
    </Section>
  );
}

/**
 * A timeline built from observations.
 *
 * The predecessor pruned its equivalent at 30 days, so the evidence for any
 * outage older than a month had evaporated and could not be recomputed. These
 * rows are kept, and this is the first screen that reads them back.
 */
function HistoryTab({ detail }: { detail: Detail }): React.JSX.Element {
  const byCheck = new Map<string, Detail['observations']>();
  for (const row of detail.observations) {
    byCheck.set(row.check_key, [...(byCheck.get(row.check_key) ?? []), row]);
  }

  if (byCheck.size === 0) {
    return <Empty title="No observations in the last seven days" />;
  }

  return (
    <Section title="History" summary="Last seven days, newest on the right. Each mark is one observation.">
      <div className="stack">
        {[...byCheck.entries()].map(([key, rows]) => (
          <div key={key}>
            <div className="small mono">{key}</div>
            <div className="sparkline">
              {[...rows].reverse().map((row, i) => (
                <span
                  key={i}
                  className={`spark ${outcomeState(row.outcome)}`}
                  title={`${row.outcome}${row.reason !== null ? ` — ${row.reason}` : ''}\n${new Date(
                    row.observed_at,
                  ).toLocaleString()}${row.latency_ms !== null ? `\n${row.latency_ms}ms` : ''}`}
                />
              ))}
            </div>
            {/* Every outcome, or the numbers do not add up. The first version
                counted pass/fail/unknown only, so a check that is always
                `unavailable` read as "7 readings · 0 · 0 · 0" — a summary that
                silently dropped the state this system exists to make visible. */}
            <div className="small muted">
              {rows.length} readings ·{' '}
              {(['pass', 'fail', 'unknown', 'unavailable'] as const)
                .map((outcome) => ({
                  outcome,
                  n: rows.filter((r) => r.outcome === outcome).length,
                }))
                .filter((entry) => entry.n > 0)
                .map((entry) => `${entry.n} ${entry.outcome}`)
                .join(' · ')}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

/** Delivery status onto the status bands. `held` is its own word, not a shade of suppressed. */
function deliveryState(status: string): PillState {
  if (status === 'sent') return 'ok';
  if (status === 'failed') return 'fault';
  if (status === 'held') return 'warning';
  return 'stale';
}

const DELIVERY_COLUMNS: ColumnDef<NotificationRow, unknown>[] = [
  {
    id: 'when',
    header: 'When',
    accessorKey: 'at',
    cell: ({ getValue }) => <span className="small mono">{new Date(getValue() as string).toLocaleString()}</span>,
  },
  {
    id: 'outcome',
    header: 'Outcome',
    accessorKey: 'status',
    // `held` is its own word, not a shade of suppressed. One is owed a
    // digest; the other is a decision nobody will revisit.
    cell: ({ getValue }) => <Pill state={deliveryState(getValue() as string)}>{getValue() as string}</Pill>,
    size: 100,
  },
  {
    id: 'channel',
    header: 'Channel',
    accessorFn: (n) => n.channel_label ?? '',
    cell: ({ row }) => (
      <span className="small">
        {row.original.channel_label ?? '—'}
        <div className="cell-sub">{row.original.routing_key ?? 'no routing key'}</div>
      </span>
    ),
  },
  {
    id: 'why',
    header: 'Why',
    accessorKey: 'reason',
    enableSorting: false,
    cell: ({ getValue }) => <span className="small muted">{(getValue() as string | null) ?? '—'}</span>,
  },
];

/**
 * Who was told about this alert, and what happened when we tried.
 *
 * 🔴 The question this answers is "why did nobody hear about this?", which the
 * predecessor could not answer at all — the reason was usually a condition
 * nobody had written down. Every outcome has been recorded since the delivery
 * subsystem was built; until now none of it was visible outside psql, and a
 * record nobody can read is most of the way back to not having one.
 *
 * Attempts come back oldest-first for a single alert, so a retry sequence reads
 * as a sequence — failed, failed, sent — rather than needing to be reassembled
 * from a reversed list.
 */
function DeliveryPanel({ alertId }: { alertId: string }): React.JSX.Element {
  const [rows, setRows] = useState<NotificationRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .get<NotificationsResponse>(`/api/notifications?alertId=${alertId}&limit=50`)
      .then((r) => { if (live) setRows(r.notifications); })
      .catch((err) => { if (live) setError(err instanceof Error ? err.message : 'failed'); });
    return (): void => { live = false; };
  }, [alertId]);

  if (error !== null) return <Banner tone="fault">{error}</Banner>;
  if (rows === null) return <Loading rows={2} cols={3} label="Loading delivery log" />;
  if (rows.length === 0) {
    // A finding rather than a blank: an alert with no notification row at all
    // was never handed to the delivery path.
    return (
      <p className="small muted">
        No delivery was attempted — this alert was never handed to the delivery path.
      </p>
    );
  }

  return (
    <DataTable
      data={rows}
      columns={DELIVERY_COLUMNS}
      getRowId={(n) => n.id}
      caption="Delivery attempts for this alert"
    />
  );
}

/**
 * Mute one check on one asset, until a stated time.
 *
 * 🔴 The interruption stops; the check does not. The row stays in this table,
 * still measured, still red when it fails, and the fault still counts
 * everywhere it counted before. A control that hid the check would be an off
 * switch for monitoring with the word "temporary" on it.
 *
 * The durations are offered as fixed choices rather than a date picker, and an
 * expiry is not optional, because the failure this guards against is a mute
 * nobody remembers setting. "Until I say otherwise" is the option deliberately
 * missing.
 */
const MUTE_CHOICES: ReadonlyArray<{ label: string; hours: number }> = [
  { label: '2 hours', hours: 2 },
  { label: 'Until tomorrow', hours: 24 },
  { label: '3 days', hours: 72 },
];

function MuteControl({
  assetId,
  checkKey,
  onChanged,
}: {
  assetId: string;
  checkKey: string;
  onChanged: () => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function mute(hours: number): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.put(`/api/assets/${assetId}/checks/${checkKey}/mute`, {
        until: new Date(Date.now() + hours * 3_600_000).toISOString(),
        reason: null,
      });
      setOpen(false);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'could not mute');
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <Button size="sm" onClick={() => setOpen(true)} disabled={busy} aria-expanded={false}>
        Mute…
      </Button>
    );
  }

  return (
    <div className="stack-tight">
      {MUTE_CHOICES.map((c) => (
        <Button key={c.hours} size="sm" busy={busy} onClick={() => void mute(c.hours)}>
          {c.label}
        </Button>
      ))}
      <Button size="sm" variant="ghost" onClick={() => setOpen(false)} disabled={busy}>
        Cancel
      </Button>
      {error !== null && <span className="caveat">{error}</span>}
    </div>
  );
}

/**
 * Alerts stay a plain table: the delivery log opens as its own row beneath
 * the alert rather than in a second drawer. It is about THIS alert and belongs
 * next to it; a nested panel over a 760px drawer is a stack nobody can back out
 * of. `DataTable` has no sub-row, so this is the one table in the drawer that
 * is not it.
 */
function AlertsTab({ detail }: { detail: Detail }): React.JSX.Element {
  const [openLog, setOpenLog] = useState<string | null>(null);

  if (detail.alerts.length === 0) {
    return <Empty tone="good" title="No alerts" body="This asset has never raised one." />;
  }
  return (
    <Section flush>
      <div className="table-wrap">
        <table className="data dense">
          <caption className="sr-only">Alerts raised by this asset</caption>
          <thead>
            <tr>
              <th>Raised</th>
              <th>Severity</th>
              <th>Cause</th>
              <th>What to do</th>
              <th>Evidence</th>
              <th>Resolved</th>
              <th>Delivery</th>
            </tr>
          </thead>
          <tbody>
            {detail.alerts.map((alert) => (
              <tr key={alert.id} className={alert.resolved_at === null && alert.severity === 'critical' ? 'row-fault' : undefined}>
                <td className="small">{new Date(alert.raised_at).toLocaleString()}</td>
                <td>
                  <Pill state={severityState(alert.severity)}>{alert.severity}</Pill>
                </td>
                <td>
                  <div>{alert.cause_label}</div>
                  <div className="cell-sub">
                    {alert.class_key}/{alert.cause_key}
                  </div>
                </td>
                <td className="small muted">{alert.operator_action ?? '—'}</td>
                {/* An alert that cannot show its evidence cannot be audited, and its
                    message will eventually describe something it never measured. */}
                <td className="small mono">
                  {Object.entries(alert.evidence ?? {})
                    .map(([k, v]) => `${k}=${String(v)}`)
                    .join(' ') || '—'}
                </td>
                <td className="small muted">
                  {alert.resolved_at === null ? (
                    <Pill state="fault">open</Pill>
                  ) : (
                    new Date(alert.resolved_at).toLocaleString()
                  )}
                </td>
                <td className="small">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setOpenLog(openLog === alert.id ? null : alert.id)}
                    aria-expanded={openLog === alert.id}
                  >
                    {openLog === alert.id ? 'Hide' : 'Show'}
                  </Button>
                </td>
              </tr>
            ))}
            {detail.alerts
              .filter((a) => a.id === openLog)
              .map((a) => (
                <tr key={`${a.id}-log`}>
                  <td colSpan={7}>
                    <DeliveryPanel alertId={a.id} />
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

const CAPABILITY_COLUMNS: ColumnDef<DetailCapability, unknown>[] = [
  {
    id: 'key',
    header: 'Capability',
    accessorKey: 'key',
    cell: ({ getValue }) => <span className="mono small">{getValue() as string}</span>,
  },
  {
    id: 'declared',
    header: 'Supported',
    accessorFn: (c) => (c.declared ? 1 : 0),
    cell: ({ row }) => (
      <Pill state={row.original.declared ? 'ok' : 'unmonitored'}>{row.original.declared ? 'yes' : 'no'}</Pill>
    ),
    size: 100,
  },
  {
    id: 'meaning',
    header: 'What it means',
    accessorFn: (c) => c.description ?? c.label,
    enableSorting: false,
    cell: ({ getValue }) => <span className="small muted">{getValue() as string}</span>,
  },
];

/**
 * What this device can and cannot be asked.
 *
 * There is no equivalent screen in the predecessor, because there was no
 * equivalent data: an Axis camera's inability to report storage was implicit in
 * the poller and invisible everywhere else, which is how the most dangerous
 * fault in the product came to be undetectable on one of two vendors.
 */
function CapabilitiesTab({ detail }: { detail: Detail }): React.JSX.Element {
  if (detail.asset.driver_id === null) {
    return (
      <Empty
        title="No driver assigned"
        body="Nothing can be asked of this asset; every check against it resolves to “not checkable”."
      />
    );
  }

  const missing = detail.capabilities.filter((c) => !c.declared);
  const summary =
    missing.length > 0
      ? `${missing.length} of ${detail.capabilities.length} not supported by ${detail.asset.driver_id}. Checks needing them report “not checkable” rather than passing or failing — the device is not broken, it cannot answer.`
      : `Everything asked of ${detail.asset.driver_id} is supported.`;

  return (
    <Section title="Capabilities" summary={summary} flush>
      <DataTable
        data={detail.capabilities}
        columns={CAPABILITY_COLUMNS}
        getRowId={(c) => c.key}
        caption="Capabilities declared by the driver"
      />
    </Section>
  );
}
